<script setup>
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { relativeDayTimestamp } from 'shared/helpers/timeHelper';
import { getLabelPillColors } from 'dashboard/helper/labelColorHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  isHighlighted: {
    type: Boolean,
    default: false,
  },
  privateNotes: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'open-lightbox',
  'open-conversation',
  'resolve-and-remove',
]);

const { t } = useI18n();
const accountLabels = useMapGetter('labels/getLabels');

const resolveLabel = title => accountLabels.value.find(l => l.title === title);

const statusColor = status => {
  return (
    {
      open: 'bg-n-teal-9',
      pending: 'bg-n-amber-9',
      resolved: 'bg-n-slate-9',
      snoozed: 'bg-n-blue-9',
    }[status] || 'bg-n-slate-9'
  );
};

// Reuses the chat-list status filter labels (already fully translated) instead of
// showing the raw conversation.status enum value.
const statusLabel = status =>
  t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${status}.TEXT`);

// Task badge: overdue takes priority over "due today", which takes priority over
// a plain "has pending tasks" state. Counters are denormalized on the conversation
// (see Conversation#update_task_counters!), so no extra request is needed here.
const isDueToday = conversation => {
  if (!conversation.next_task_due_at) return false;
  const dueDate = new Date(conversation.next_task_due_at * 1000);
  const now = new Date();
  return dueDate.toDateString() === now.toDateString();
};

const taskBadgeVariant = conversation => {
  if (conversation.overdue_tasks_count > 0) return 'overdue';
  if (conversation.pending_tasks_count > 0 && isDueToday(conversation)) {
    return 'due_today';
  }
  if (conversation.pending_tasks_count > 0) return 'pending';
  return null;
};

const taskBadgeClasses = {
  overdue: 'bg-n-ruby-9 text-white',
  due_today: 'bg-n-amber-9 text-n-slate-12',
  pending: 'bg-n-slate-4 text-n-slate-11',
};

const taskBadgeLabel = conversation => {
  const variant = taskBadgeVariant(conversation);
  if (variant === 'overdue') {
    return t('KANBAN.TASKS.OVERDUE', { count: conversation.overdue_tasks_count });
  }
  if (variant === 'due_today') return t('KANBAN.TASKS.DUE_TODAY');
  if (variant === 'pending') {
    return t('KANBAN.TASKS.PENDING', { count: conversation.pending_tasks_count });
  }
  return '';
};
</script>

<template>
  <div
    class="relative p-4 transition-all bg-n-solid-1 rounded-lg border border-n-weak hover:border-n-ruby-8/50 hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] cursor-grab group"
    :data-conversation-id="conversation.id"
    :class="{
      'ring-2 ring-n-ruby-8 ring-offset-2': isHighlighted,
    }"
    @click="emit('open-lightbox', conversation)"
  >
    <button
      v-tooltip.top="t('KANBAN.CONTACT_LIGHTBOX.OPEN_FULL_CONVERSATION')"
      class="absolute z-10 items-center justify-center hidden rounded-md size-6 top-2 ltr:right-9 rtl:left-9 bg-n-solid-2 border border-n-weak text-n-slate-11 hover:text-n-ruby-11 group-hover:flex"
      @click.stop="emit('open-conversation', conversation)"
    >
      <Icon icon="i-lucide-arrow-up-right" class="size-3.5" />
    </button>
    <button
      v-tooltip.top="t('KANBAN.RESOLVE_CARD.BUTTON_TOOLTIP')"
      class="absolute z-10 items-center justify-center hidden rounded-md size-6 top-2 ltr:right-2 rtl:left-2 bg-n-solid-2 border border-n-weak text-n-slate-11 hover:text-n-ruby-9 group-hover:flex"
      @click.stop="emit('resolve-and-remove', conversation)"
    >
      <Icon icon="i-lucide-trash-2" class="size-3.5" />
    </button>

    <div
      v-if="conversation.labels?.length"
      class="flex flex-wrap items-start justify-between gap-1 mb-2 pr-6"
    >
      <div class="flex flex-wrap gap-1 min-w-0">
        <span
          v-for="labelTitle in conversation.labels"
          :key="labelTitle"
          class="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
          :style="{
            backgroundColor: getLabelPillColors(
              resolveLabel(labelTitle)?.color || '#8a8a8a'
            ).bg,
            color: getLabelPillColors(resolveLabel(labelTitle)?.color || '#8a8a8a')
              .text,
          }"
        >
          {{ labelTitle }}
        </span>
      </div>
      <span class="text-[11px] text-n-slate-10 shrink-0">
        {{ relativeDayTimestamp(conversation.timestamp) }}
      </span>
    </div>

    <div class="flex items-start gap-2 mb-1">
      <Avatar
        :src="conversation.meta?.sender?.thumbnail"
        :name="conversation.meta?.sender?.name || ''"
        :size="28"
      />
      <div class="flex-1 min-w-0">
        <h3
          class="text-sm font-semibold truncate text-n-slate-12 group-hover:text-n-ruby-11 pr-6"
        >
          {{ conversation.meta?.sender?.name }}
        </h3>
        <p class="text-xs truncate text-n-slate-11">
          {{
            conversation.meta?.sender?.phone_number ||
            conversation.meta?.sender?.email
          }}
        </p>
      </div>
      <span
        v-if="!conversation.labels?.length"
        class="text-[11px] text-n-slate-10 shrink-0"
      >
        {{ relativeDayTimestamp(conversation.timestamp) }}
      </span>
    </div>

    <div
      v-if="taskBadgeVariant(conversation) || privateNotes.length"
      class="flex flex-wrap items-center gap-1.5 mb-1"
    >
      <span
        v-if="taskBadgeVariant(conversation)"
        class="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold shrink-0"
        :class="taskBadgeClasses[taskBadgeVariant(conversation)]"
      >
        <Icon icon="i-lucide-check-square" class="size-3" />
        {{ taskBadgeLabel(conversation) }}
      </span>
      <span
        v-if="privateNotes.length"
        class="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold shrink-0 bg-n-amber-9/10 text-n-amber-11 border border-n-amber-9/20"
      >
        <Icon icon="i-lucide-sticky-note" class="size-3 text-n-amber-9" />
        {{ t('KANBAN.CARD.HAS_NOTES') }}
      </span>
    </div>

    <div
      class="flex items-center justify-between gap-2 pt-2 mt-2 border-t border-n-weak"
    >
      <span
        class="flex items-center gap-1 text-[11px] font-medium text-n-slate-10 truncate"
      >
        <span
          class="rounded-full size-1.5 shrink-0"
          :class="statusColor(conversation.status)"
        />
        {{ statusLabel(conversation.status) }}
      </span>
      <Avatar
        v-if="conversation.meta?.assignee"
        :src="conversation.meta.assignee.thumbnail"
        :name="conversation.meta.assignee.name || ''"
        :size="20"
        class="shrink-0"
      />
    </div>
  </div>
</template>
